import { type ChunkReader, streamChunks, chunkSize } from './chunk'
import type { FileUploadData } from './FileUploadData'
import { request } from '../util/request'
import {
    type StartMultipartUploadEndpoint,
    route as startMultipartUploadRoute,
} from '../../../server/http/files/startMultipartUpload.no-deps'
import {
    type GenerateUploadPartUrlEndpoint,
    route as generateUploadPartUrlRoute,
} from '../../../server/http/files/generateUploadPartUrl.no-deps'
import {
    type CompleteMultipartUploadEndpoint,
    route as completeMultipartUploadRoute,
} from '../../../server/http/files/completeMultipartUpload.no-deps'
import type { ApiClient } from '../util/ApiClient'

export interface SendToS3Args {
    fileUploadData: FileUploadData
    reader: ChunkReader
    apiClient: ApiClient
    onProgress: (bytesUploaded: number) => void
    abortSignal?: AbortSignal
}

const calculateProgress = (chunkIndex: number, fileSizeInBytes: number, bytesUploaded: number = 0) =>
    Math.min(chunkIndex * chunkSize + bytesUploaded, fileSizeInBytes)

export async function sendToS3(args: SendToS3Args) {
    const { fileUploadData, reader, apiClient, onProgress, abortSignal } = args
    const { fileId, fileSizeInBytes, mimeType } = fileUploadData

    const { uploadId } = (await startUpload(apiClient, { fileId, mimeType })).body

    // eTags are a concept from AWs multipart upload which identify each part of the upload
    const eTags: string[] = []

    await streamChunks(fileSizeInBytes, reader, async (chunkIndex, chunk) => {
        if (abortSignal?.aborted) {
            throw abortSignal.reason
        }

        const { uploadUrl } = (
            await generateUploadPartUrl(apiClient, {
                part: chunkIndex + 1,
                fileId,
                uploadId,
            })
        ).body
        const response = await uploadPart(uploadUrl, chunk, (bytesUploaded) => {
            onProgress(calculateProgress(chunkIndex, fileSizeInBytes, bytesUploaded))
        })
        onProgress(calculateProgress(chunkIndex + 1, fileSizeInBytes))
        eTags.push(response.headers.etag)
    })

    await completeUpload(apiClient, { eTags, uploadId, fileId })
}

const startUpload = (apiClient: ApiClient, body: StartMultipartUploadEndpoint['Body']) => {
    return apiClient.request<StartMultipartUploadEndpoint['Body'], StartMultipartUploadEndpoint['Output']>({
        method: startMultipartUploadRoute.method,
        route: startMultipartUploadRoute.pattern,
        body,
    })
}

const generateUploadPartUrl = (apiClient: ApiClient, body: GenerateUploadPartUrlEndpoint['Body']) => {
    return apiClient.request<GenerateUploadPartUrlEndpoint['Body'], GenerateUploadPartUrlEndpoint['Output']>({
        method: generateUploadPartUrlRoute.method,
        route: generateUploadPartUrlRoute.pattern,
        body,
    })
}

const uploadPart = (url: string, body: ArrayBuffer, onProgress: (bytesUploaded: number) => void) => {
    return request<ArrayBuffer, void>(
        {
            method: 'PUT',
            url,
            body,
            headers: {},
        },
        onProgress
    )
}

const completeUpload = (apiClient: ApiClient, body: CompleteMultipartUploadEndpoint['Body']) => {
    return apiClient.request<CompleteMultipartUploadEndpoint['Body'], CompleteMultipartUploadEndpoint['Output']>({
        method: completeMultipartUploadRoute.method,
        route: completeMultipartUploadRoute.pattern,
        body,
    })
}
